import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import eventifyLogo from 'assets/eventify-logo.png';
import api from 'services/api';
import PasswordInput from 'components/PasswordInput';
import { useAuth } from 'context/AuthContext';
import SearchableSelect from 'components/SearchableSelect';
import PoliticaCheckbox from 'components/PoliticaCheckbox';
import styles from './Register.module.css';
import { EMAIL_REGEX } from 'utils/email';

const TIPOS_ID = ['CC', 'CC_DIG', 'NUIP'];
// bcrypt solo usa los primeros 72 bytes de la clave: el backend rechaza más.
const bytesUtf8 = (s) => new TextEncoder().encode(s).length;

// ── Paso 1: Verificar propietario ─────────────────────────────────────────────
function StepVerify({ onFound, onAlreadyRegistered }) {
  const [tipoId,  setTipoId]  = useState('CC');
  const [docNum,  setDocNum]  = useState('');
  const [email,   setEmail]   = useState('');
  const [byEmail, setByEmail] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const verify = async (e) => {
    e.preventDefault();
    setError('');
    // noValidate en el <form>: estas revisiones reemplazan el globo del navegador.
    if (!byEmail && !docNum.trim()) return setError('Ingresa tu número de documento');
    if (byEmail && !email.trim())   return setError('Ingresa tu correo electrónico');
    if (byEmail && !EMAIL_REGEX.test(email.trim())) return setError('El correo no es válido');
    setLoading(true);
    try {
      const payload = byEmail
        ? { email }
        : { tipo_identificacion: tipoId, numero_identificacion: docNum };
      const { data } = await api.post('/auth/verify-owner', payload);
      onFound(data.owner);
    } catch (err) {
      const msg = err.response?.data?.message || 'Error al verificar';
      const status = err.response?.status;
      // 409 = ya tiene cuenta → ofrecer recuperación de contraseña
      if (status === 409) {
        onAlreadyRegistered(byEmail ? email : null);
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={verify} noValidate>
      {error && (
        <div className={`alert alert-danger ${styles.alerta}`}>
          <i className={`fas fa-circle-exclamation ${styles.alertaIcono}`} />{error}
        </div>
      )}
      <div className={styles.conmutador}>
        <button type="button"
          onClick={() => { setByEmail(false); setError(''); }}
          className={`btn btn-sm ${!byEmail ? 'btn-primary' : 'btn-secondary'} ${styles.botonModo}`}>
          <i className={`fas fa-id-card ${styles.iconoBtn}`} />Documento
        </button>
        <button type="button"
          onClick={() => { setByEmail(true); setError(''); }}
          className={`btn btn-sm ${byEmail ? 'btn-primary' : 'btn-secondary'} ${styles.botonModo}`}>
          <i className={`fas fa-envelope ${styles.iconoBtn}`} />Correo
        </button>
      </div>

      {!byEmail ? (
        <>
          <div className="form-group">
            <label className="form-label">Tipo de documento *</label>
            <SearchableSelect value={tipoId} onChange={(v) => setTipoId(v)} minWidth={0} maxWidth={9999} allowEmpty={false} ariaLabel="Tipo de documento" options={TIPOS_ID.map(t => ({ value: t, label: t }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Número de documento *</label>
            <input className="form-control" value={docNum} onChange={e => setDocNum(e.target.value)}
              placeholder="Ej: 1234567890" maxLength={50} required />
          </div>
        </>
      ) : (
        <div className="form-group">
          <label className="form-label">Correo electrónico *</label>
          <input type="email" className="form-control" value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="tu@correo.com" maxLength={255} required />
        </div>
      )}

      <div className={styles.avisoAlta}>
        <i className={`fas fa-info-circle ${styles.avisoAltaIcono}`} />
        Solo pueden registrarse propietarios dados de alta por el administrador de su conjunto.
      </div>

      <button type="submit" className={`btn btn-primary w-full ${styles.centrado}`} disabled={loading}>
        {loading
          ? <><div className={`spinner ${styles.spinnerChico}`} /> Verificando...</>
          : <><i className={`fas fa-search ${styles.iconoBtn}`} />Verificar propietario</>}
      </button>
    </form>
  );
}

// ── Paso 2: Verificar OTP ──────────────────────────────────────────────────────
function StepOTP({ owner, onVerified, onBack }) {
  const [otp,     setOtp]     = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error,   setError]   = useState('');
  const [sent,    setSent]    = useState(false);

  const sendOTP = async () => {
    setSending(true); setError('');
    try {
      // D3: se identifica por documento; el servidor busca el correo (la pantalla
      // solo conoce el correo enmascarado).
      await api.post('/auth/send-otp', { tipo_identificacion: owner.tipo_identificacion, numero_identificacion: owner.numero_identificacion, motivo: 'registro' });
      setSent(true);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al enviar el código');
    } finally { setSending(false); }
  };

  const verify = async (e) => {
    e.preventDefault();
    if (!sent) return setError('Primero solicita el código OTP');
    if (!/^\d{6}$/.test(otp.trim())) return setError('El código debe tener 6 dígitos');
    setLoading(true); setError('');
    try {
      await api.post('/auth/verify-otp', { tipo_identificacion: owner.tipo_identificacion, numero_identificacion: owner.numero_identificacion, otp: otp.trim() });
      onVerified();
    } catch (err) {
      const msg = err.response?.data?.message || 'Código incorrecto o expirado';
      setError(msg);
      // Si expiró, limpiar para que vuelva a solicitar
      if (msg.includes('expirado') || msg.includes('expiró')) {
        setOtp(''); setSent(false);
      }
    } finally { setLoading(false); }
  };

  return (
    <div>
      {error && (
        <div className={`alert alert-danger ${styles.alerta}`}>
          <i className={`fas fa-circle-exclamation ${styles.alertaIcono}`} />{error}
        </div>
      )}

      {/* Info del correo */}
      <div className={styles.cajaDato}>
        <div className={styles.cajaTitulo}>
          <i className={`fas fa-envelope ${styles.iconoAcento}`} />
          Código OTP
        </div>
        <p className={styles.textoOtp}>
          Te enviaremos un código de 6 dígitos al correo:
        </p>
        <p className={styles.correoOtp}>
          {owner.email}
        </p>
      </div>

      {/* Botón enviar OTP */}
      {!sent ? (
        <button type="button" className={`btn btn-primary w-full ${styles.botonEnviar}`}
          onClick={sendOTP} disabled={sending}>
          {sending
            ? <><div className={`spinner ${styles.spinnerChico}`} /> Enviando...</>
            : <><i className={`fas fa-paper-plane ${styles.iconoBtn}`} />Enviar código OTP</>}
        </button>
      ) : (
        <div className={styles.otpEnviado}>
          <i className="fas fa-check-circle" />
          Código enviado. Válido por 10 minutos.
          <button type="button" onClick={sendOTP} disabled={sending}
            className={styles.reenviar}>
            Reenviar
          </button>
        </div>
      )}

      {/* Input OTP */}
      <form onSubmit={verify} noValidate>
        <div className="form-group">
          <label className="form-label">Código de verificación *</label>
          <input className={`form-control ${styles.campoOtp}`} value={otp} onChange={e => setOtp(e.target.value)}
            placeholder="Ingresa el código de 6 dígitos"
            maxLength={6}
            required disabled={!sent} />
        </div>

        <div className={styles.dosBotones}>
          <button type="button" className={`btn btn-secondary ${styles.centrado}`}
            onClick={onBack}>
            <i className={`fas fa-arrow-left ${styles.iconoBtn}`} />Volver
          </button>
          <button type="submit" className={`btn btn-primary ${styles.centrado}`}
            disabled={loading || !sent}>
            {loading
              ? <><div className={`spinner ${styles.spinnerChico}`} />Verificando...</>
              : <><i className={`fas fa-check ${styles.iconoBtn}`} />Verificar código</>}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Paso 3: Crear contraseña ───────────────────────────────────────────────────
function StepAccount({ owner, onBack, onSubmit, loading, error }) {
  const [password, setPassword] = useState('');
  const [confirm,  setConfirm]  = useState('');
  const [acepta,   setAcepta]   = useState(false);
  const [localErr, setLocalErr] = useState('');
  // El aviso va arriba y «Crear cuenta» abajo: en un celular de 568 px de alto quedaba fuera de la vista y parecía que el botón
  // no hacía nada. `intentos` trae el aviso a la vista aunque un segundo intento repita el MISMO texto.
  const errorRef = useRef(null);
  const [intentos, setIntentos] = useState(0);

  const submit = (e) => {
    e.preventDefault();
    setLocalErr('');
    setIntentos(n => n + 1);
    if (password.length < 8) return setLocalErr('La contraseña debe tener mínimo 8 caracteres');
    if (!password.trim()) return setLocalErr('La contraseña no puede ser solo espacios');
    if (bytesUtf8(password) > 72) return setLocalErr('La contraseña es demasiado larga (máximo 72 caracteres sin tildes ni ñ)');
    if (password !== confirm) return setLocalErr('Las contraseñas no coinciden');
    if (!acepta) return setLocalErr('Debes aceptar la Política de Tratamiento de Datos Personales para crear tu cuenta');
    onSubmit(password, acepta);
  };

  const err = localErr || error;
  useEffect(() => { if (err) errorRef.current?.scrollIntoView({ block: 'nearest' }); }, [err, intentos]);

  return (
    <form onSubmit={submit} noValidate>
      {err && (
        <div ref={errorRef} className={`alert alert-danger ${styles.alerta}`}>
          <i className={`fas fa-circle-exclamation ${styles.alertaIcono}`} />{err}
        </div>
      )}

      {/* Datos del owner */}
      <div className={`${styles.cajaDato} ${styles.cajaIdentidad}`}>
        <div className={`${styles.cajaTitulo} ${styles.cajaTituloIdentidad}`}>
          <i className={`fas fa-check-circle ${styles.iconoOk}`} />
          Identidad verificada
        </div>
        <div className={styles.datosGrid}>
          <div>
            <div className={styles.datoEtiqueta}>Nombre</div>
            <div className={styles.datoValor}>{owner.nombre} {owner.apellido}</div>
          </div>
          <div>
            <div className={styles.datoEtiqueta}>Documento</div>
            <div className={styles.datoValor}>{owner.tipo_identificacion} {owner.numero_identificacion}</div>
          </div>
          <div className={styles.datoCompleto}>
            <div className={styles.datoEtiqueta}>Correo</div>
            <div className={styles.datoValor}>{owner.email}</div>
          </div>
        </div>
      </div>

      <div className={styles.camposPassword}>
        <div className="form-group">
          <label className="form-label">Contraseña *</label>
          <PasswordInput value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder="Mínimo 8 caracteres" autoComplete="new-password" maxLength={72} required />
        </div>
        <div className="form-group">
          <label className="form-label">Confirmar contraseña *</label>
          <PasswordInput value={confirm}
            onChange={e => setConfirm(e.target.value)}
            placeholder="Repite tu contraseña" autoComplete="new-password" maxLength={72} required />
        </div>
      </div>

      <PoliticaCheckbox checked={acepta} onChange={setAcepta} />

      <div className={styles.dosBotones}>
        <button type="button" className={`btn btn-secondary ${styles.centrado}`}
          onClick={onBack}>
          <i className={`fas fa-arrow-left ${styles.iconoBtn}`} />Volver
        </button>
        <button type="submit" className={`btn btn-primary ${styles.centrado}`}
          disabled={loading}>
          {loading
            ? <><div className={`spinner ${styles.spinnerChico}`} />Creando...</>
            : <><i className={`fas fa-check ${styles.iconoBtn}`} />Crear cuenta</>}
        </button>
      </div>
    </form>
  );
}

// ── Pantalla: Usuario ya registrado ───────────────────────────────────────────
function AlreadyRegistered({ email, onBack }) {
  return (
    <div className={styles.yaRegistrado}>
      <div className={styles.yaRegistradoIcono}>
        <i className={`fas fa-user-check ${styles.yaRegistradoGlifo}`} />
      </div>
      <h3 className={styles.yaRegistradoTitulo}>¡Ya tienes una cuenta!</h3>
      <p className={styles.yaRegistradoTexto}>
        {email
          ? <>El correo <strong>{email}</strong> ya tiene una cuenta registrada.</>
          : <>Este propietario ya tiene una cuenta registrada.</>}
      </p>
      <div className={styles.yaRegistradoBotones}>
        <Link to="/login" className={`btn btn-primary w-full ${styles.centrado}`}>
          <i className={`fas fa-sign-in-alt ${styles.iconoBtn}`} />Iniciar sesión
        </Link>
        <Link to="/change-password" className={`btn btn-secondary w-full ${styles.centrado}`}>
          <i className={`fas fa-key ${styles.iconoBtn}`} />Recuperar / cambiar contraseña
        </Link>
        <button type="button" className={`btn btn-secondary w-full ${styles.centrado}`}
          onClick={onBack}>
          <i className={`fas fa-arrow-left ${styles.iconoBtn}`} />Volver
        </button>
      </div>
    </div>
  );
}

// ── Componente principal ───────────────────────────────────────────────────────
export default function Register() {
  const navigate = useNavigate();
  const { iniciarSesionConToken } = useAuth();

  const [step,    setStep]    = useState(0);   // 0:verificar, 1:OTP, 2:contraseña, 3:ya-existe
  const [owner,   setOwner]   = useState(null);
  const [existEmail, setExistEmail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  const handleOwnerFound = (ownerData) => {
    setOwner(ownerData);
    setStep(1);  // ir a OTP
    setError('');
  };

  const handleAlreadyRegistered = (email) => {
    setExistEmail(email);
    setStep(3);  // ir a pantalla "ya tienes cuenta"
  };

  const handleOTPVerified = () => {
    setStep(2);  // ir a crear contraseña
  };

  const handleSubmit = async (password, aceptaPolitica) => {
    setError('');
    setLoading(true);
    try {
      const payload = {
        tipo_identificacion:   owner.tipo_identificacion,
        numero_identificacion: owner.numero_identificacion,
        password,
        acepta_politica:       aceptaPolitica,
      };
      const { data } = await api.post('/auth/register', payload);
      await iniciarSesionConToken(data.token, data.user); // D4: entra directo a su panel
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Error al registrarse');
    } finally {
      setLoading(false);
    }
  };

  // Labels de pasos
  const steps = ['Verificar identidad', 'Confirmar correo', 'Crear contraseña'];

  return (
    <div className="auth-page">
      <div className={`auth-card ${styles.tarjeta}`}>

        <div className="auth-logo">
          <div className={styles.logoCaja}>
            <img src={eventifyLogo} alt="EVENTIFY" className={`auth-logo-img ${styles.logoImg}`} />
          </div>
          <p>Crea tu cuenta de propietario</p>
        </div>

        {/* Indicador de pasos (solo pasos 0-2) */}
        {step < 3 && (
          <div className={styles.pasos}>
            {steps.map((label, idx) => {
              const active = step === idx;
              const done   = step > idx;
              return (
                <div key={idx} className={styles.paso}>
                  <div className={`${styles.pasoCuerpo} ${active || done ? styles.pasoVivo : ''}`}>
                    <div className={`${styles.pasoCirculo} ${done || active ? styles.pasoCirculoVivo : ''}`}>
                      {done ? <i className={`fas fa-check ${styles.pasoCheck}`} /> : idx + 1}
                    </div>
                    <span className={`${styles.pasoEtiqueta} ${active ? styles.pasoEtiquetaActiva : 'step-label-inactive'}`}>
                      {label}
                    </span>
                  </div>
                  {idx < steps.length - 1 && (
                    <div className={styles.pasoLinea} />
                  )}
                </div>
              );
            })}
          </div>
        )}

        {step === 0 && (
          <StepVerify
            onFound={handleOwnerFound}
            onAlreadyRegistered={handleAlreadyRegistered}
          />
        )}
        {step === 1 && owner && (
          <StepOTP
            owner={owner}
            onVerified={handleOTPVerified}
            onBack={() => setStep(0)}
          />
        )}
        {step === 2 && owner && (
          <StepAccount
            owner={owner}
            onBack={() => setStep(1)}
            onSubmit={handleSubmit}
            loading={loading}
            error={error}
          />
        )}
        {step === 3 && (
          <AlreadyRegistered
            email={existEmail}
            onBack={() => setStep(0)}
          />
        )}

        <p className="text-center text-muted mt-4">
          ¿Ya tienes cuenta? <Link to="/login">Inicia sesión</Link>
        </p>
      </div>
    </div>
  );
}